import {Service, service, inject} from "./di";
import {StorageService} from "./StorageService";
import {SeoService} from "./SeoService";
import {Backflipp} from "./BackflippService";
import { ConfigService } from "./ConfigService";
import {SeoDataService} from "./SeoDataService";
import {slugify} from "../utils/slugify";

export enum Country {
	CANADA,
	USA
}

export interface LocationInfo {
	postal_code: string;
	city_name: string;
	country_code: string;
	region_name: string;
}

export interface StateConfig {
	slug: string;
}

@service
export class LocationService extends Service {
	@inject storage: StorageService;
	@inject backflipp: Backflipp;
	@inject seoService: SeoService;
	@inject config: ConfigService;
	@inject dataService: SeoDataService;

	get postalCode() {
		return document.documentElement.getAttribute("postalcode");
	}

	get geoCode() {
		const code = new PostalCode(this.postalCode);
		return code.fsa;
	}

	get country() {
		const code = new PostalCode(this.postalCode);
		return code.country;
	}

	get city() {
		return document.documentElement.getAttribute("city");
	}

	get locale() {
		return this.seoService.locale;
	}

	getInCity(): string {
		if (!this.seoService.inCity) {
			let city = this.city;
			if (!city) {
				try {
					const info = this.getStoredInfo();
					city = info.city_name;
				} catch {
					if (!city) {
						return null;
					}
				}
			}
			return `${slugify(city.trim())}-${this.state}`.toLowerCase();
		}

		return this.seoService.inCity;
	}

	getLocaleBasedCountry(): string {
		return this.isUsLocale(this.locale) ? "usa" : "canada";
	}

	get state() {
		const info = this.getStoredInfo();
		return info && info.region_name ? info.region_name.toUpperCase().trim() : "";
	}

	async getLocationInfo(): Promise<LocationInfo> {
		const storedInfo = this.getStoredInfo();

		if (storedInfo) return storedInfo;
		return this.findLocation();
	}

	async savePostalCode(code: PostalCode): Promise<LocationInfo> {
		// if the postal code being saved is already stored, we don't need to
		// do anything.
		const stored = this.getStoredInfo();
		if (stored) {
			const storedPC = new PostalCode(stored.postal_code);
			if (PostalCode.isEqual(code, storedPC)) {
				return stored;
			}
		}

		// with a new postal code, we need to get the other data from backflipp
		return this.findLocation(code.postalCode);
	}

	async findLocation(postalCode: string = null) {
		const info = await this.backflipp.getLocationInfo(postalCode);
		this.prepareInfo(info);

		this.storage.setItem("location_info", JSON.stringify(info));
		return info;
	}

	prepareInfo(info: LocationInfo) {
		if (!info.city_name) {
			info.city_name = "";
		}

		if (!info.postal_code) {
			info.postal_code = "";
		}

		if (!info.country_code) {
			info.country_code = "";
		}

		if (!info.region_name) {
			info.region_name = "";
		}
	}

	acceptsLocale(loc: string) {
		return this.locale === loc || Boolean(this.locale.match(new RegExp(`${loc}[-_]`, "i")));
	}

	getStateToStateTextMapping = (): {[uri: string]: string} => ({
		"ON" : "Ontario",
	})

	isUsLocale(locale: string): boolean {
		return locale === "en-us";
	}

	isCanadianLocale(locale: string): boolean {
		return locale === "en-ca" || locale === "fr-ca";
	}

	getStateName(config: StateConfig): string {
		const {slug} = config;
		const upperSlug = slug.toUpperCase();
		const state = this.isUsLocale(this.locale) ? this.dataService.getUsStates()[upperSlug] : this.dataService.getCaProvinces()[upperSlug];
		return state || upperSlug;
	}

	getStoredInfo(): LocationInfo {
		const stored = this.storage.getItem("location_info");
		return stored ? JSON.parse(stored) : null;
	}

	getLocaleFromAPI() {
		const storedInfo = this.getStoredInfo();
		if (storedInfo !== null) {
			const { country_code, region_name } = storedInfo;
			if (region_name && region_name.trim() === "QC") {
				return "fr-ca";
			}
			if (country_code) {
				return `en-${country_code.toLowerCase()}`;
			}
		}

		return this.locale;
	}
}

const MATCHER_USA = /^\d{5}$/;
const MATCHER_CANADA = /^[A-Za-z][0-9][A-Za-z][ ]?[0-9][A-Za-z][0-9]$/;
export let MATCHER_POSTAL_CODE = new RegExp(`(${MATCHER_USA.source})|(${MATCHER_CANADA.source})`);

export class PostalCode {

	static isValid(code: string): boolean {
		return code.search(MATCHER_USA) >= 0 || code.search(MATCHER_CANADA) >= 0;
	}

	static isEqual(a: PostalCode, b: PostalCode): boolean {
		return a.country === b.country &&
					a.fsa === b.fsa &&
					a.ldu === b.ldu;
	}

	constructor(private value: string) {
		value = SeoService.getInstance().fixTrailingSlash(value);

		if (!PostalCode.isValid(value)) {
			throw new Error("Postal code is invalid");
		}
	}

	get postalCode(): string {
		return this.value;
	}

	get country(): Country {
		if (this.value.search(MATCHER_USA) >= 0) {
			return Country.USA;
		} else if (this.value.search(MATCHER_CANADA) >= 0) {
			return Country.CANADA;
		}
	}

	get geoCode() {
		const postalCode = this.postalCode;
		if (this.country === Country.CANADA && postalCode && postalCode.length > 0)
			return this.fsa;
		else
			return postalCode;
	}

	get fsa(): string {
		return this.postalCode.substr(0, 3);
	}

	get ldu(): string {
		return this.postalCode.replace(/\s/g, "").substr(3, this.postalCode.length - 1);
	}
}
